import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { MediaDto } from '@/types/dtos/media/media.dto';
import type { FlowMediaPlaylistsDto, MediaPlaylistDto } from '@/types/dtos/media/media-playlist.dto';
import { NON_SLOT_FLOW_PLAYLIST_SCOPES } from '@/types/dtos/media/flow-playlist-scope.enum';
import type { GetSessionsDto } from '@/types/dtos/sessions/response/get-sessions.dto';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';
import type { GetMediaResponseDto } from '@/types/dtos/media/response/get-media.dto';
import { getObjectId } from '@/helpers/object.id.helper';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session';
import { GET_EVENT_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-event-sessions';
import { GET_MEDIA_QUERY_KEY } from '@/state/queries/media/use-get-media';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useEventStore } from '@/state/stores/use-event-store';

export type MediaAssetApprovalPatch = {
  mediaId: string;
  assetVersion: number;
  isApproved: boolean;
  sessionOriginId?: string;
};

function sessionMatchesKey(session: SessionDto, sessionKey: string): boolean {
  const origin = session.origin ? getObjectId(session.origin) : session.id;
  return session.id === sessionKey || origin === sessionKey;
}

export function updateMediaApprovalOnMediaList(
  mediaList: MediaDto[] | undefined,
  mediaId: string,
  assetVersion: number,
  isApproved: boolean,
): MediaDto[] | undefined {
  if (!mediaList?.length) return mediaList;
  let changed = false;
  const nextMedia = mediaList.map((media) => {
    if (media.id !== mediaId || !media.assets?.length) return media;
    const nextAssets = media.assets.map((asset) => {
      if (asset.version !== assetVersion) return asset;
      if (asset.isApproved === isApproved) return asset;
      changed = true;
      return { ...asset, isApproved };
    });
    if (nextAssets === media.assets) return media;
    return { ...media, assets: nextAssets };
  });
  return changed ? nextMedia : mediaList;
}

function patchMediaDto(media: MediaDto, patch: MediaAssetApprovalPatch): MediaDto {
  if (media.id !== patch.mediaId || !media.assets?.length) return media;
  const nextAssets = media.assets.map((asset) => {
    if (asset.version !== patch.assetVersion) return asset;
    if (asset.isApproved === patch.isApproved) return asset;
    return { ...asset, isApproved: patch.isApproved };
  });
  if (nextAssets === media.assets) return media;
  return { ...media, assets: nextAssets };
}

/** Playlist rows prefer embedded `ref.media` over `session.media` — patch both. */
function patchMediaPlaylist(
  playlist: MediaPlaylistDto | undefined,
  patch: MediaAssetApprovalPatch,
): MediaPlaylistDto | undefined {
  if (!playlist?.refs?.length) return playlist;
  let changed = false;
  const refs = playlist.refs.map((ref) => {
    if (ref.mediaId !== patch.mediaId && ref.media?.id !== patch.mediaId) return ref;
    if (!ref.media) return ref;
    const patchedMedia = patchMediaDto(ref.media, patch);
    if (patchedMedia === ref.media) return ref;
    changed = true;
    return { ...ref, media: patchedMedia };
  });
  return changed ? { ...playlist, refs } : playlist;
}

function patchFlowMediaPlaylists(
  mediaPlaylists: FlowMediaPlaylistsDto | undefined,
  patch: MediaAssetApprovalPatch,
): FlowMediaPlaylistsDto | undefined {
  if (!mediaPlaylists) return mediaPlaylists;
  let changed = false;
  const next: FlowMediaPlaylistsDto = { ...mediaPlaylists };

  for (const scope of NON_SLOT_FLOW_PLAYLIST_SCOPES) {
    const patched = patchMediaPlaylist(next[scope], patch);
    if (patched && patched !== next[scope]) {
      next[scope] = patched;
      changed = true;
    }
  }

  if (next.slots) {
    const slots = { ...next.slots };
    for (const [slotId, playlist] of Object.entries(slots)) {
      const patched = patchMediaPlaylist(playlist, patch);
      if (patched && patched !== playlist) {
        slots[slotId] = patched;
        changed = true;
      }
    }
    next.slots = slots;
  }

  return changed ? next : mediaPlaylists;
}

function patchSessionEntity(session: SessionDto, patch: MediaAssetApprovalPatch): SessionDto {
  if (patch.sessionOriginId && !sessionMatchesKey(session, patch.sessionOriginId)) {
    return session;
  }

  let next = session;
  const nextMedia = updateMediaApprovalOnMediaList(session.media, patch.mediaId, patch.assetVersion, patch.isApproved);
  if (nextMedia && nextMedia !== session.media) {
    next = { ...next, media: nextMedia };
  }

  const patchedSessionPlaylist = patchMediaPlaylist(session.mediaPlaylists?.session, patch);
  if (patchedSessionPlaylist && patchedSessionPlaylist !== session.mediaPlaylists?.session) {
    next = {
      ...next,
      mediaPlaylists: {
        ...next.mediaPlaylists,
        session: patchedSessionPlaylist,
      },
    };
  }

  return next === session ? session : next;
}

function patchFlowEntity(flow: FlowDto, patch: MediaAssetApprovalPatch): FlowDto {
  let nextFlow = flow;
  const nextFlowMedia = updateMediaApprovalOnMediaList(
    nextFlow.media,
    patch.mediaId,
    patch.assetVersion,
    patch.isApproved,
  );
  if (nextFlowMedia && nextFlowMedia !== nextFlow.media) {
    nextFlow = { ...nextFlow, media: nextFlowMedia };
  }

  const patchedPlaylists = patchFlowMediaPlaylists(nextFlow.mediaPlaylists, patch);
  if (patchedPlaylists && patchedPlaylists !== nextFlow.mediaPlaylists) {
    nextFlow = { ...nextFlow, mediaPlaylists: patchedPlaylists };
  }

  if (!nextFlow.slots?.length) return nextFlow;

  let slotsChanged = false;
  const nextSlots = nextFlow.slots.map((slot) => {
    if (slot.contentType !== 'Session' || typeof slot.content !== 'object' || !slot.content) {
      return slot;
    }
    const sessionContent = slot.content as SessionDto;
    const patchedContent = patchSessionEntity(sessionContent, patch);
    if (patchedContent === sessionContent) return slot;
    slotsChanged = true;
    return { ...slot, content: patchedContent };
  });

  return slotsChanged ? { ...nextFlow, slots: nextSlots } : nextFlow;
}

function patchFlowQueryData(data: FlowDto | FlowDto[], patch: MediaAssetApprovalPatch): FlowDto | FlowDto[] {
  if (Array.isArray(data)) {
    return data.map((flow) => patchFlowEntity(flow, patch));
  }
  return patchFlowEntity(data, patch);
}

function patchGetSessionsPage(page: GetSessionsDto, patch: MediaAssetApprovalPatch): GetSessionsDto {
  let changed = false;
  const nextData = page.data.map((session) => {
    const patched = patchSessionEntity(session, patch);
    if (patched !== session) changed = true;
    return patched;
  });
  return changed ? { ...page, data: nextData } : page;
}

type EventSessionsPage =
  | GetSessionsDto
  | GetSessionsDto[]
  | [GetSessionsDto | GetSessionsDto[], GetSessionsDto | GetSessionsDto[]];

function patchEventSessionsPage(page: EventSessionsPage, patch: MediaAssetApprovalPatch): EventSessionsPage {
  if (Array.isArray(page)) {
    return page.map((entry) => {
      if (Array.isArray(entry)) {
        return entry.map((nested) => patchGetSessionsPage(nested, patch));
      }
      return patchGetSessionsPage(entry, patch);
    }) as EventSessionsPage;
  }
  return patchGetSessionsPage(page, patch);
}

function patchEventSessionsQueryData(data: unknown, patch: MediaAssetApprovalPatch): unknown {
  if (!data || typeof data !== 'object') return data;

  if ('pages' in data && Array.isArray((data as InfiniteData<EventSessionsPage>).pages)) {
    const infinite = data as InfiniteData<EventSessionsPage>;
    return {
      ...infinite,
      pages: infinite.pages.map((page) => patchEventSessionsPage(page, patch)),
    };
  }

  if (Array.isArray(data)) {
    return (data as SessionDto[]).map((session) => patchSessionEntity(session, patch));
  }

  return data;
}

function patchMediaInfiniteData(data: InfiniteData<GetMediaResponseDto>, patch: MediaAssetApprovalPatch) {
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      data: page.data.map((media) => patchMediaDto(media, patch)),
    })),
  };
}

function patchSessionListInEventStore(
  sessions: SessionDto[] | undefined,
  patch: MediaAssetApprovalPatch,
): SessionDto[] | undefined {
  if (!sessions) return sessions;
  let changed = false;
  const next = sessions.map((session) => {
    const patched = patchSessionEntity(session, patch);
    if (patched !== session) changed = true;
    return patched;
  });
  return changed ? next : sessions;
}

export function patchMediaAssetApprovalInEventStore(patch: MediaAssetApprovalPatch) {
  const state = useEventStore.getState();
  const eventSessionsPlan = patchSessionListInEventStore(state.eventSessionsPlan, patch);
  const eventSessionsOrganize = patchSessionListInEventStore(state.eventSessionsOrganize, patch);
  const eventSessions = patchSessionListInEventStore(state.eventSessions, patch);

  if (
    eventSessionsPlan !== state.eventSessionsPlan ||
    eventSessionsOrganize !== state.eventSessionsOrganize ||
    eventSessions !== state.eventSessions
  ) {
    useEventStore.setState({
      ...(eventSessionsPlan !== state.eventSessionsPlan ? { eventSessionsPlan } : {}),
      ...(eventSessionsOrganize !== state.eventSessionsOrganize ? { eventSessionsOrganize } : {}),
      ...(eventSessions !== state.eventSessions ? { eventSessions } : {}),
    });
  }
}

export function patchMediaAssetApprovalInFlowStore(patch: MediaAssetApprovalPatch) {
  useFlowStore.setState((state) => {
    const patchFlowDocument = (flow?: FlowDto) => (flow ? patchFlowEntity(flow, patch) : flow);

    const flow = patchFlowDocument(state.flow);
    const flowPlan = patchFlowDocument(state.flowPlan);
    const flowOrganize = patchFlowDocument(state.flowOrganize);

    if (flow === state.flow && flowPlan === state.flowPlan && flowOrganize === state.flowOrganize) {
      return state;
    }

    return { flow, flowPlan, flowOrganize };
  });
}

export function patchMediaAssetApprovalInQueryCache(queryClient: QueryClient, patch: MediaAssetApprovalPatch) {
  for (const [queryKey, data] of queryClient.getQueriesData<FlowDto | FlowDto[]>({
    queryKey: [GET_FLOW_QUERY_KEY],
  })) {
    if (!data) continue;
    queryClient.setQueryData(queryKey, patchFlowQueryData(data, patch));
  }

  for (const [queryKey, data] of queryClient.getQueriesData<FlowDto[]>({
    queryKey: [GET_EVENT_FLOWS_QUERY_KEY],
  })) {
    if (!data || !Array.isArray(data)) continue;
    queryClient.setQueryData(
      queryKey,
      data.map((flow) => patchFlowEntity(flow, patch)),
    );
  }

  for (const [queryKey, data] of queryClient.getQueriesData<SessionDto>({
    queryKey: [GET_SESSION_QUERY_KEY],
  })) {
    if (!data) continue;
    queryClient.setQueryData(queryKey, patchSessionEntity(data, patch));
  }

  for (const [queryKey, data] of queryClient.getQueriesData({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY] })) {
    if (!data) continue;
    queryClient.setQueryData(queryKey, patchEventSessionsQueryData(data, patch));
  }

  for (const [queryKey, data] of queryClient.getQueriesData<InfiniteData<GetMediaResponseDto>>({
    queryKey: [GET_MEDIA_QUERY_KEY],
  })) {
    if (!data?.pages) continue;
    queryClient.setQueryData(queryKey, patchMediaInfiniteData(data, patch));
  }

  patchMediaAssetApprovalInFlowStore(patch);
  patchMediaAssetApprovalInEventStore(patch);
}
